import React from 'react'
import { ArrowRight, Check, Play, MessageCircle, Clock, TrendingUp, Smartphone } from 'lucide-react'

export default function Hero() {
  return (
    <section id="hero" className="relative min-h-screen flex items-center overflow-hidden hero-gradient">
      {/* Subtle Background Pattern */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none opacity-30">
        <div className="absolute top-20 left-10 w-72 h-72 bg-white/10 rounded-full blur-3xl" />
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-white/10 rounded-full blur-3xl" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-20">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-8 items-center">

          {/* LEFT SIDE - Text Content */}
          <div className="text-center lg:text-left">
            {/* Trust Badge */}
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/20 backdrop-blur-sm rounded-full text-white text-sm font-medium border border-white/30 mb-6">
              <span className="flex h-2 w-2 relative">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-green-400"></span>
              </span>
              Ginagamit ng 50+ laundry shops
            </div>

            {/* Main Headline - Tagalog, Pain-focused */}
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
              Wag Na Mag-Notebook.
              <br />
              <span className="text-cyan-200">Mag-LaundryMate Na.</span>
            </h1>

            {/* Subheadline - Specific benefits */}
            <p className="text-lg sm:text-xl text-white/90 mb-8 max-w-xl mx-auto lg:mx-0">
              Order tracking, SMS reminders, loyalty program, inventory -
              <strong className="text-white"> lahat nasa isang app lang.</strong>
            </p>

            {/* Quick Benefits */}
            <div className="flex flex-col sm:flex-row gap-4 sm:gap-6 justify-center lg:justify-start mb-8 text-white/90">
              <div className="flex items-center gap-2">
                <div className="w-5 h-5 rounded-full bg-green-400/20 flex items-center justify-center">
                  <Check className="w-3 h-3 text-green-400" />
                </div>
                <span className="text-sm">Setup in 1 hour</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-5 h-5 rounded-full bg-green-400/20 flex items-center justify-center">
                  <Check className="w-3 h-3 text-green-400" />
                </div>
                <span className="text-sm">Only P***/month</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-5 h-5 rounded-full bg-green-400/20 flex items-center justify-center">
                  <Check className="w-3 h-3 text-green-400" />
                </div>
                <span className="text-sm">Cancel anytime</span>
              </div>
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start mb-10">
              <a
                href="https://app.laundrymate.shop"
                className="group inline-flex items-center justify-center gap-2 bg-white text-cyan-600 px-8 py-4 rounded-xl font-bold text-lg transition-all duration-200 shadow-xl hover:shadow-2xl hover:scale-105 active:scale-95"
              >
                Get Started Now
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </a>
              <a
                href="https://m.me/946243658562372"
                target="_blank"
                rel="noopener noreferrer"
                className="group inline-flex items-center justify-center gap-2 bg-white/10 backdrop-blur-sm text-white px-8 py-4 rounded-xl font-semibold text-lg border-2 border-white/30 transition-all duration-200 hover:bg-white/20 hover:scale-105 active:scale-95"
              >
                <MessageCircle className="w-5 h-5" />
                Chat with Us
              </a>
            </div>

            {/* Social Proof Stats */}
            <div className="grid grid-cols-3 gap-4 max-w-md mx-auto lg:mx-0">
              <div className="text-center lg:text-left">
                <div className="text-2xl sm:text-3xl font-bold text-white">50+</div>
                <div className="text-xs sm:text-sm text-white/70">Active Shops</div>
              </div>
              <div className="text-center lg:text-left">
                <div className="text-2xl sm:text-3xl font-bold text-white">10K+</div>
                <div className="text-xs sm:text-sm text-white/70">Orders/Month</div>
              </div>
              <div className="text-center lg:text-left">
                <div className="text-2xl sm:text-3xl font-bold text-white">98%</div>
                <div className="text-xs sm:text-sm text-white/70">Satisfaction</div>
              </div>
            </div>
          </div>

          {/* RIGHT SIDE - App Preview */}
          <div className="relative flex justify-center lg:justify-end">
            {/* Phone Mockup */}
            <div className="relative">
              {/* Glow Effect */}
              <div className="absolute inset-0 bg-cyan-400/30 blur-3xl rounded-full scale-75" />

              {/* Phone Frame with Real Screenshot */}
              <div className="relative bg-gray-900 rounded-[2.5rem] p-1.5 shadow-2xl border-4 border-gray-800">
                <div className="bg-gray-900 rounded-[2rem] overflow-hidden">
                  {/* Real App Screenshot - shows full app including bottom nav */}
                  <img
                    src="/dashboard-mobile.png"
                    alt="LaundryMate Dashboard"
                    className="w-[280px] sm:w-[320px] h-auto block"
                  />
                </div>
              </div>

              {/* Floating Elements */}
              <div className="absolute -left-4 top-20 bg-white rounded-xl p-3 shadow-xl animate-bounce-slow hidden sm:block">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                    <TrendingUp className="w-4 h-4 text-green-600" />
                  </div>
                  <div>
                    <div className="text-xs text-gray-500">Revenue</div>
                    <div className="text-sm font-bold text-gray-800">+35%</div>
                  </div>
                </div>
              </div>

              <div className="absolute -right-4 top-40 bg-white rounded-xl p-3 shadow-xl animate-bounce-slow hidden sm:block" style={{ animationDelay: '0.5s' }}>
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                    <Clock className="w-4 h-4 text-blue-600" />
                  </div>
                  <div>
                    <div className="text-xs text-gray-500">Time Saved</div>
                    <div className="text-sm font-bold text-gray-800">3hrs/day</div>
                  </div>
                </div>
              </div>

              <div className="absolute -left-2 bottom-32 bg-white rounded-xl p-3 shadow-xl animate-bounce-slow hidden sm:block" style={{ animationDelay: '1s' }}>
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center">
                    <Smartphone className="w-4 h-4 text-purple-600" />
                  </div>
                  <div>
                    <div className="text-xs text-gray-500">SMS Sent</div>
                    <div className="text-sm font-bold text-gray-800">Auto!</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce hidden lg:block">
        <div className="w-6 h-10 border-2 border-white/50 rounded-full flex justify-center pt-2">
          <div className="w-1 h-3 bg-white/50 rounded-full" />
        </div>
      </div>

      {/* Add bounce-slow animation */}
      <style>{`
        @keyframes bounce-slow {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-10px); }
        }
        .animate-bounce-slow {
          animation: bounce-slow 3s ease-in-out infinite;
        }
      `}</style>
    </section>
  )
}
